/**
 * Browser Information Utilities
 * Helper functions to detect and extract browser/device information
 */

import {
  DeviceType,
  BrowserInfo,
} from "../api-data/iam/browser-tracking/types";

/**
 * Detects the device type based on user agent
 */
export function getDeviceType(): DeviceType {
  const ua = navigator.userAgent.toLowerCase();

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return DeviceType.TABLET;
  }

  if (
    /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua
    )
  ) {
    return DeviceType.MOBILE;
  }

  return DeviceType.DESKTOP;
}

/**
 * Extracts the browser name from user agent
 */
export function getBrowserName(): string {
  const ua = navigator.userAgent;

  if (ua.includes("Edg/") || ua.includes("Edge/")) return "Edge";
  if (ua.includes("Chrome/") && !ua.includes("Edg/")) return "Chrome";
  if (ua.includes("Safari/") && !ua.includes("Chrome/")) return "Safari";
  if (ua.includes("Firefox/")) return "Firefox";
  if (ua.includes("Opera/") || ua.includes("OPR/")) return "Opera";
  if (ua.includes("MSIE") || ua.includes("Trident/"))
    return "Internet Explorer";

  return "Unknown";
}

/**
 * Extracts the browser version from user agent
 */
export function getBrowserVersion(): string {
  const ua = navigator.userAgent;
  const browserName = getBrowserName();

  let match: RegExpMatchArray | null = null;

  switch (browserName) {
    case "Edge":
      match = ua.match(/Edg?\/([\d.]+)/);
      break;
    case "Chrome":
      match = ua.match(/Chrome\/([\d.]+)/);
      break;
    case "Safari":
      match = ua.match(/Version\/([\d.]+)/);
      break;
    case "Firefox":
      match = ua.match(/Firefox\/([\d.]+)/);
      break;
    case "Opera":
      match = ua.match(/(?:Opera|OPR)\/([\d.]+)/);
      break;
    case "Internet Explorer":
      match = ua.match(/(?:MSIE |rv:)([\d.]+)/);
      break;
  }

  return match ? match[1] : "Unknown";
}

/**
 * Extracts the operating system name
 */
export function getOS(): string {
  const ua = navigator.userAgent;
  const platform = navigator.platform;

  if (/Win/i.test(platform)) return "Windows";
  if (/Mac/i.test(platform)) return "macOS";
  if (/Linux/i.test(platform)) return "Linux";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Android/i.test(ua)) return "Android";

  return platform || "Unknown";
}

/**
 * Extracts device manufacturer from user agent
 */
export function getDeviceManufacturer(): string {
  const ua = navigator.userAgent;

  // Apple devices
  if (/iPhone|iPad|iPod|Macintosh/i.test(ua)) return "Apple";

  // Samsung
  if (/Samsung|SM-|SAMSUNG/i.test(ua)) return "Samsung";

  // Xiaomi
  if (/Xiaomi|MI |Redmi|POCO/i.test(ua)) return "Xiaomi";

  // Huawei
  if (/Huawei|HUAWEI|Honor|HONOR/i.test(ua)) return "Huawei";

  // OnePlus
  if (/OnePlus|ONEPLUS/i.test(ua)) return "OnePlus";

  // Google
  if (/Pixel/i.test(ua)) return "Google";

  // Oppo
  if (/OPPO|CPH/i.test(ua)) return "Oppo";

  // Vivo
  if (/vivo|VIVO/i.test(ua)) return "Vivo";

  // Motorola
  if (/Motorola|Moto|XT[0-9]/i.test(ua)) return "Motorola";

  // Nokia
  if (/Nokia/i.test(ua)) return "Nokia";

  // LG
  if (/LG-|LG |LGE/i.test(ua)) return "LG";

  // Sony
  if (/Sony|SonyEricsson/i.test(ua)) return "Sony";

  // HTC
  if (/HTC/i.test(ua)) return "HTC";

  // Realme
  if (/Realme|RMX/i.test(ua)) return "Realme";

  // Asus
  if (/ASUS|Zenfone/i.test(ua)) return "Asus";

  // Lenovo
  if (/Lenovo/i.test(ua)) return "Lenovo";

  // Generic Android
  if (/Android/i.test(ua)) return "Android Device";

  return "Unknown";
}

/**
 * Extracts device model from user agent
 */
export function getDeviceModel(): string {
  const ua = navigator.userAgent;

  // iPhone models
  const iphoneMatch = ua.match(/iPhone(\d+[,\d]*)/i);
  if (iphoneMatch) return `iPhone ${iphoneMatch[1].replace(/,/g, ".")}`;
  if (/iPhone/i.test(ua)) return "iPhone";

  // iPad models
  const ipadMatch = ua.match(/iPad(\d+[,\d]*)/i);
  if (ipadMatch) return `iPad ${ipadMatch[1].replace(/,/g, ".")}`;
  if (/iPad/i.test(ua)) return "iPad";

  // Mac models
  if (/Macintosh/i.test(ua)) {
    if (/Intel Mac OS X/i.test(ua)) return "Mac";
    return "Macintosh";
  }

  // Samsung models (SM-xxxx format)
  const samsungMatch = ua.match(/SM-([A-Z0-9]+)/i);
  if (samsungMatch) return `SM-${samsungMatch[1]}`;

  // Xiaomi models
  const xiaomiMatch = ua.match(/(?:Redmi|MI|POCO) ([A-Z0-9 ]+)/i);
  if (xiaomiMatch) return xiaomiMatch[1].trim();

  // Huawei/Honor models
  const huaweiMatch = ua.match(/(?:HUAWEI|Honor) ([A-Z0-9-]+)/i);
  if (huaweiMatch) return huaweiMatch[1];

  // OnePlus models
  const oneplusMatch = ua.match(/ONEPLUS ([A-Z0-9]+)/i);
  if (oneplusMatch) return oneplusMatch[1];

  // Google Pixel models
  const pixelMatch = ua.match(/Pixel( [0-9A-Z ]+)?/i);
  if (pixelMatch) return pixelMatch[0].trim();

  // Oppo models
  const oppoMatch = ua.match(/(?:OPPO |CPH)([A-Z0-9]+)/i);
  if (oppoMatch) return `CPH${oppoMatch[1]}`;

  // Vivo models
  const vivoMatch = ua.match(/vivo ([A-Z0-9]+)/i);
  if (vivoMatch) return vivoMatch[1];

  // Motorola models
  const motoMatch = ua.match(/Moto ([A-Z0-9 ]+)|XT([0-9]+)/i);
  if (motoMatch) return motoMatch[0].trim();

  // Nokia models
  const nokiaMatch = ua.match(/Nokia ([A-Z0-9.]+)/i);
  if (nokiaMatch) return nokiaMatch[1];

  // LG models
  const lgMatch = ua.match(/LG-([A-Z0-9]+)|LGE ([A-Z0-9]+)/i);
  if (lgMatch) return lgMatch[1] || lgMatch[2];

  // Sony models
  const sonyMatch = ua.match(/(?:Sony|SonyEricsson) ([A-Z0-9]+)/i);
  if (sonyMatch) return sonyMatch[1];

  // HTC models
  const htcMatch = ua.match(/HTC[_ ]?([A-Z0-9+ ]+)/i);
  if (htcMatch) return htcMatch[1].trim();

  // Realme models
  const realmeMatch = ua.match(/(?:Realme |RMX)([A-Z0-9]+)/i);
  if (realmeMatch) return `RMX${realmeMatch[1]}`;

  // Asus models
  const asusMatch = ua.match(/ASUS[_ ]?([A-Z0-9_]+)|Zenfone ([A-Z0-9 ]+)/i);
  if (asusMatch) return (asusMatch[1] || asusMatch[2]).trim();

  // Lenovo models
  const lenovoMatch = ua.match(/Lenovo ([A-Z0-9-]+)/i);
  if (lenovoMatch) return lenovoMatch[1];

  // Generic model extraction (try to find model-like patterns)
  const genericMatch = ua.match(/Build\/([A-Z0-9]+)/i);
  if (genericMatch) return genericMatch[1];

  return "Unknown";
}

/**
 * Extracts the operating system version
 */
export function getOSVersion(): string {
  const ua = navigator.userAgent;

  // Windows
  if (/Windows NT 10.0/i.test(ua)) return "10";
  if (/Windows NT 6.3/i.test(ua)) return "8.1";
  if (/Windows NT 6.2/i.test(ua)) return "8";
  if (/Windows NT 6.1/i.test(ua)) return "7";

  // macOS
  const macMatch = ua.match(/Mac OS X ([\d._]+)/);
  if (macMatch) return macMatch[1].replace(/_/g, ".");

  // iOS
  const iosMatch = ua.match(/OS ([\d_]+)/);
  if (iosMatch) return iosMatch[1].replace(/_/g, ".");

  // Android
  const androidMatch = ua.match(/Android ([\d.]+)/);
  if (androidMatch) return androidMatch[1];

  return "Unknown";
}

/**
 * Generates a simple hash from a string (for browser fingerprinting)
 */
export function generateSimpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Get screen characteristics
 */
function getScreenFingerprint(): string {
  const screen = window.screen;
  return [
    screen.width,
    screen.height,
    screen.availWidth,
    screen.availHeight,
    screen.colorDepth,
    screen.pixelDepth,
    window.devicePixelRatio || 1,
  ].join(",");
}

/**
 * Generate canvas fingerprint
 * Canvas rendering varies slightly between browsers and devices
 */
function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return "no-canvas";

    canvas.width = 200;
    canvas.height = 50;

    // Draw text with specific styling
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("BrowserFP", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Canvas 123", 4, 17);

    // Get canvas data
    const dataURL = canvas.toDataURL();
    return generateSimpleHash(dataURL).toString();
  } catch (e) {
    return "canvas-error";
  }
}

/**
 * Get WebGL renderer info
 */
function getWebGLFingerprint(): string {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "no-webgl";

    const debugInfo = (gl as any).getExtension("WEBGL_debug_renderer_info");
    if (!debugInfo) return "no-debug-info";

    const vendor = (gl as any).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const renderer = (gl as any).getParameter(
      debugInfo.UNMASKED_RENDERER_WEBGL
    );

    return `${vendor}~${renderer}`;
  } catch (e) {
    return "webgl-error";
  }
}

/**
 * Get available fonts (sample check)
 */
function getFontsFingerprint(): string {
  const baseFonts = ["monospace", "sans-serif", "serif"];
  const testFonts = [
    "Arial",
    "Verdana",
    "Times New Roman",
    "Courier New",
    "Georgia",
    "Palatino",
    "Garamond",
    "Comic Sans MS",
    "Trebuchet MS",
    "Impact",
  ];

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return "no-fonts";

  const baseFontWidths: { [key: string]: number } = {};

  // Measure base fonts
  baseFonts.forEach((baseFont) => {
    ctx.font = `72px ${baseFont}`;
    baseFontWidths[baseFont] = ctx.measureText("mmmmmmmmmmlli").width;
  });

  const availableFonts: string[] = [];

  // Check each test font
  testFonts.forEach((font) => {
    baseFonts.forEach((baseFont) => {
      ctx.font = `72px ${font}, ${baseFont}`;
      const width = ctx.measureText("mmmmmmmmmmlli").width;

      if (width !== baseFontWidths[baseFont]) {
        if (!availableFonts.includes(font)) {
          availableFonts.push(font);
        }
      }
    });
  });

  return availableFonts.sort().join(",");
}

/**
 * Get audio context fingerprint
 */
function getAudioFingerprint(): string {
  try {
    const AudioContext =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) return "no-audio";

    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const analyser = context.createAnalyser();
    const gainNode = context.createGain();
    const scriptProcessor = context.createScriptProcessor(4096, 1, 1);

    gainNode.gain.value = 0; // Mute
    oscillator.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.start(0);

    const fingerprint = [
      context.sampleRate,
      analyser.frequencyBinCount,
      context.destination.maxChannelCount,
    ].join(",");

    oscillator.stop();
    context.close();

    return fingerprint;
  } catch (e) {
    return "audio-error";
  }
}

/**
 * Get touch support
 */
function getTouchSupport(): string {
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const touchEvent = "ontouchstart" in window;
  const touchPoints = (navigator as any).msMaxTouchPoints || 0;

  return `${maxTouchPoints},${touchEvent},${touchPoints}`;
}

/**
 * Get additional browser features
 */
function getBrowserFeatures(): string {
  return [
    typeof (window as any).openDatabase !== "undefined",
    typeof (navigator as any).sendBeacon !== "undefined",
    typeof window.localStorage !== "undefined",
    typeof window.sessionStorage !== "undefined",
    typeof window.indexedDB !== "undefined",
    (navigator as any).doNotTrack || "unknown",
    navigator.cookieEnabled,
  ].join(",");
}

/**
 * Creates an enhanced browser fingerprint key based on multiple characteristics
 * This provides much better uniqueness than basic user agent fingerprinting
 */
export function generateBrowserFingerprintKey(): number {
  const components = [
    // Basic info
    navigator.userAgent,
    navigator.language,
    navigator.languages?.join(",") || "",
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    navigator.platform,

    // Screen info
    getScreenFingerprint(),

    // Canvas fingerprint (very unique)
    getCanvasFingerprint(),

    // WebGL info
    getWebGLFingerprint(),

    // Fonts
    getFontsFingerprint(),

    // Audio context
    getAudioFingerprint(),

    // Touch support
    getTouchSupport(),

    // Browser features
    getBrowserFeatures(),
  ].join("|");

  return generateSimpleHash(components);
}

/**
 * Collects all browser information in one call
 */
export function collectBrowserInfo(): BrowserInfo {
  return {
    deviceType: getDeviceType(),
    os: getOS(),
    osVersion: getOSVersion(),
    browserName: getBrowserName(),
    browserVersion: getBrowserVersion(),
    userAgent: navigator.userAgent,
    deviceManufacturer: getDeviceManufacturer(),
    deviceModel: getDeviceModel(),
  };
}

/**
 * Generates a unique session ID
 */
export function generateSessionId(): string {
  return `sess_${Math.random().toString(36).substr(2, 9)}${Date.now().toString(
    36
  )}`;
}
